import { useCallback, useRef, useState } from 'react'

import { Box, Icon, Text, VStack } from '@chakra-ui/react'
import { LuFileCheck, LuUpload } from 'react-icons/lu'

/** A file drop zone: click to pick a file or drop one onto it. */
const FileDrop = ({
  label,
  formats,
  prompt,
  orDrop,
  accept,
  file,
  existingFileName,
  onSelect,
  disabled = false,
  readOnly = false,
}: {
  /** Field label. */
  label: string
  /** Accepted file formats, shown as a hint. */
  formats: string
  /** "Choose a file" call to action. */
  prompt: string
  /** " or drop it here" suffix. */
  orDrop: string
  /** `accept` attribute for the file input (e.g. `.las,.laz,.ply`). */
  accept: string
  /** Currently selected file, if any. */
  file: File | null
  /** Name of the file already uploaded to the area, shown until a new one is picked. */
  existingFileName?: string
  /** Called with the picked/dropped file. */
  onSelect: (file: File) => void
  /** Disables picking (e.g. while uploading). */
  disabled?: boolean
  /**
   * Show the existing file but don't allow picking a new one — the box stays to display the
   * filename with no upload affordance (e.g. a point cloud, which can't be replaced once set).
   */
  readOnly?: boolean
}) => {
  // Refs
  const inputRef = useRef<HTMLInputElement>(null)

  // States
  const [isDragOver, setIsDragOver] = useState(false)

  // Read-only shows the filename only; disabled blocks picking while an upload is in flight.
  let cursor: 'default' | 'not-allowed' | 'pointer' = 'pointer'
  if (readOnly) cursor = 'default'
  else if (disabled) cursor = 'not-allowed'

  /**
   * Accept a dropped file.
   * @param e Drop event carrying the file.
   */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      if (disabled) return
      const dropped = e.dataTransfer.files?.[0]
      if (dropped) onSelect(dropped)
    },
    [disabled, onSelect],
  )

  /**
   * Accept a file picked through the hidden input.
   * @param e Change event from the file input.
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = e.target.files?.[0]
      if (picked) onSelect(picked)
      // Allow re-picking the same file.
      e.target.value = ''
    },
    [onSelect],
  )

  return (
    // Fill the grid cell so paired drop zones stay the same height even when only
    // one of them shows a filename.
    <Box display="flex" flexDirection="column" h="100%">
      <Text fontSize="13px" fontWeight="semibold" color="gray.700">
        {label}
      </Text>
      <Text fontSize="12px" color="gray.400" mt="2px" mb="8px">
        {formats}
      </Text>
      <VStack
        flex="1"
        justify="center"
        gap="8px"
        py="22px"
        px="14px"
        border="1.5px dashed"
        borderColor={isDragOver ? 'primary.400' : 'gray.300'}
        borderRadius="11px"
        bg={isDragOver ? 'primary.50' : 'gray.50'}
        textAlign="center"
        cursor={cursor}
        opacity={disabled ? 0.6 : 1}
        onClick={() => !disabled && !readOnly && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled && !readOnly) setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={readOnly ? undefined : handleDrop}
        data-testid={`file-drop-${accept.replace(/[.,]/g, '')}`}
      >
        {!readOnly && (
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleInputChange}
            disabled={disabled}
            style={{ display: 'none' }}
          />
        )}
        <Box
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="36px"
          h="36px"
          borderRadius="9px"
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          color="primary.600"
        >
          <Icon as={file || existingFileName ? LuFileCheck : LuUpload} boxSize="18px" />
        </Box>
        {file && (
          <Text fontSize="13px" color="gray.800" fontWeight="semibold" maxW="100%" truncate>
            {file.name}
          </Text>
        )}
        {!file && existingFileName && (
          <Text fontSize="13px" color="gray.500" maxW="100%" truncate>
            {existingFileName}
          </Text>
        )}
        {!file && !readOnly && (
          <Text fontSize="13px" color="gray.700">
            <Text as="span" color="primary.600" fontWeight="semibold">
              {prompt}
            </Text>
            {orDrop}
          </Text>
        )}
      </VStack>
    </Box>
  )
}

export default FileDrop
